import React, { useEffect, useState } from 'react';
import { collection, getDocs, addDoc, updateDoc, doc, getDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../../firebaseConfig';
import Swal from 'sweetalert2';
import Navbar from '../../components/navbar/navbar';
import FooterTop from '../../components/footer-top';
import Footer from '../../components/footer';
import SEO from '../../components/SEO';
import { generateCollectionPageSchema } from '../../utils/schema';

export default function SubmitProject() {
  const [formData, setFormData] = useState({
    nombre: '',
    descripcion: '',
    direccion: '',
    terreno_m2: '',
    amenidades: [] as string[],
    agencias: [
      {
        inmobiliaria_id: '',
      }
    ]
  });

  const [inmobiliarias, setInmobiliarias] = useState<{ id: string; nombre: string; inmobiliaria_id: string }[]>([]);
  const amenidadesDisponibles = ['Parque', 'Balcón', 'Área verde', 'Wi-Fi', 'Estacionamiento', 'Jacuzzi'];

  useEffect(() => {
    const fetchInmobiliarias = async () => {
      const snap = await getDocs(collection(db, 'inmobiliaria'));
      const datos = snap.docs.map(doc => ({
        id: doc.id,
        nombre: doc.data().nombre,
        inmobiliaria_id: doc.data().inmobiliaria_id
      }));
      setInmobiliarias(datos);
    };
    fetchInmobiliarias();
  }, []);

  const generarIdProject = (nombre: string, tipo: string): string => {
    const n = nombre.charAt(0).toLowerCase();
    const t = tipo.charAt(0).toLowerCase();
    const num = Math.floor(10000 + Math.random() * 89999).toString();
    return `${n}${t}${num}`;
  };

  const handleAmenidades = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { value, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      amenidades: checked
        ? [...prev.amenidades, value]
        : prev.amenidades.filter(a => a !== value)
    }));
  };

  const handleAgenciaChange = (index: number, value: string) => {
    const nuevas = [...formData.agencias];
    nuevas[index].inmobiliaria_id = value;
    setFormData(prev => ({ ...prev, agencias: nuevas }));
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      const idGenerado = generarIdProject(formData.nombre, 'proyecto');
      const inmobiliariaDoc = await getDoc(doc(db, 'inmobiliaria', formData.agencias[0].inmobiliaria_id));
      const inmobiliariaData = inmobiliariaDoc.exists() ? inmobiliariaDoc.data() : null;
      const inmobiliariaIdCustom = inmobiliariaData?.inmobiliaria_id || '';

      const proyecto = {
        proyecto_id: idGenerado,
        inmobiliaria_id: inmobiliariaIdCustom,
        nombre: formData.nombre,
        descripcion: formData.descripcion,
        direccion: formData.direccion,
        area_m2: parseFloat(formData.terreno_m2),
        amenidades: formData.amenidades,
        propiedad_id: [],
        fechaRegistro: serverTimestamp()
      };

      await addDoc(collection(db, 'proyectos'), proyecto);

      if (inmobiliariaDoc.exists()) {
        const proyectosPrevios = inmobiliariaData?.proyecto_id || [];
        const actualizado = Array.from(new Set([...proyectosPrevios, idGenerado]));
        await updateDoc(doc(db, 'inmobiliaria', formData.agencias[0].inmobiliaria_id), {
          proyecto_id: actualizado
        });
      }

      Swal.fire({
        icon: 'success',
        title: '¡Éxito!',
        text: 'Proyecto registrado exitosamente',
        confirmButtonColor: '#3085d6'
      });
    } catch (err) {
      console.error(err);
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: 'No se pudo registrar el proyecto',
        confirmButtonColor: '#d33'
      });
    }
  };

  // Schema.org para la página de registrar proyecto
  const collectionSchema = generateCollectionPageSchema(
    'Registrar Proyecto Inmobiliario',
    'Registra nuevos proyectos inmobiliarios en AMPI Riviera Nayarit. Gestiona proyectos residenciales y comerciales',
    '/modulob/submit-project'
  )

  return (
    <>
      <SEO
        title="Registrar Proyecto Inmobiliario"
        description="Registra nuevos proyectos inmobiliarios en AMPI Riviera Nayarit. Gestiona proyectos residenciales y comerciales"
        path="/modulob/submit-project"
        type="website"
        schema={collectionSchema}
      />
      <Navbar transparent={false} />
      <section className="py-5">
        <div className="container">
          <div className="row justify-content-center">
            <div className="col-xl-10 col-lg-11 col-md-12">
              <div className="card border-0 rounded-4 p-4 shadow-sm">
                <h2 className="text-center mb-4 fw-bold" style={{ color: '#0c2339' }}>
                  Ficha de Registro de Proyecto
                </h2>

                <form onSubmit={handleSubmit} className="taller-form-modern">
                  {/* Sección 1: Información del Proyecto */}
                  <div className="card mb-4">
                    <div className="card-header bg-primary text-white">
                      <h5 className="mb-0">1. Información del Proyecto</h5>
                    </div>
                    <div className="card-body">
                      <div className="row">
                        <div className="form-group col-md-6">
                          <label>Nombre <span className="text-danger">*</span></label>
                          <input name="nombre" onChange={handleChange} className="form-control" placeholder="Nombre del proyecto" required />
                        </div>
                        <div className="form-group col-md-6">
                          <label>Terreno Total (m²) <span className="text-danger">*</span></label>
                          <input name="terreno_m2" onChange={handleChange} className="form-control" placeholder="Ej. 5000" required />
                        </div>
                        <div className="form-group col-md-12">
                          <label>Descripción <span className="text-danger">*</span></label>
                          <textarea name="descripcion" onChange={handleChange} className="form-control h-120" placeholder="Descripción del proyecto" required />
                        </div>
                        <div className="form-group col-md-12">
                          <label>Dirección (URL de ubicación) <span className="text-danger">*</span></label>
                          <input name="direccion" onChange={handleChange} className="form-control" placeholder="https://maps.google.com/..." required />
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Sección 2: Amenidades */}
                  <div className="card mb-4">
                    <div className="card-header bg-primary text-white">
                      <h5 className="mb-0">2. Amenidades</h5>
                    </div>
                    <div className="card-body">
                      <div className="row">
                        {amenidadesDisponibles.map((a, idx) => (
                          <div className="form-group col-md-3" key={idx}>
                            <input type="checkbox" value={a} onChange={handleAmenidades} /> {a}
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>

                  {/* Sección 3: Agencia Responsable */}
                  <div className="card mb-4">
                    <div className="card-header bg-primary text-white">
                      <h5 className="mb-0">3. Agencia Responsable</h5>
                    </div>
                    <div className="card-body">
                      <div className="row">
                        <div className="form-group col-md-6">
                          <label>Agencia <span className="text-danger">*</span></label>
                          <select
                            value={formData.agencias[0]?.inmobiliaria_id}
                            onChange={(e) => handleAgenciaChange(0, e.target.value)}
                            className="form-control"
                            required
                          >
                            <option value="">Selecciona una agencia</option>
                            {inmobiliarias.map(i => (
                              <option key={i.id} value={i.id}>{i.nombre}</option>
                            ))}
                          </select>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Botón de submit */}
                  <div className="text-center mt-4">
                    <button type="submit" className="btn btn-primary px-5">
                      <i className="fa fa-folder-plus me-2"></i>
                      Registrar Proyecto
                      <i className="fa-solid fa-arrow-right-long ms-2"></i>
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      </section>

      <FooterTop bg="theme-bg" />
      <Footer />
    </>
  );
}
